import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Main as Container } from 'Components/Main';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { getDataById } from 'services/datas-service';
import { PageHeader, Button, EditSvg } from 'Components/ui';
import { formatDate as formatDateIntl, LOCALE_MAP } from 'utils/locale-formatting';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';

const TEXT = 'var(--vo-ink-2)';
const DIVIDER = 'var(--vo-cloud)';

const ArchiveBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { border-color: ${MUTED}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
`;

/* ── Left info card ── */
const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const InfoCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${DIVIDER};
`;
const FieldsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const FieldRow = styled.div``;
const FieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 6px;
`;
const FieldValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${TEXT};
  margin: 0;
  line-height: 1.65;
`;
const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const TagChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-celeste);
  background: var(--vo-capri-50);
  padding: 3px 12px;
  border-radius: 20px;
`;

/* ── Right history card ── */
const HistCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const HistTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${DIVIDER};
`;
const HistBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const HistItem = styled.div``;
const HistValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  margin: 0;
`;
const CountValue = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${BLUE};
  margin: 0;
`;

export const DataShow = () => {
  const { t, i18n } = useTranslation(['mapping']);
  const navigate = useNavigate();
  const { id } = useParams();
  const locale = LOCALE_MAP[i18n.language] ?? 'pt-BR';
  const formatDate = (iso) => formatDateIntl(iso, locale) || '—';

  const { isLoading, error, data } = useQuery(['getDataById', id], async () => {
    const resData = await getDataById(id);
    if (resData.status === 200) return resData.data;
    return null;
  });

  return (
    <Container>
      <PageHeader
        title={data?.name ?? ''}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('mapping:dataTypes.list.title'), route: '/data' },
          { label: data?.name ?? '' },
        ]}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate('/data/edit/' + id)}
              startIcon={<EditSvg />}
              sx={{ height: '40px', padding: '0 16px' }}
            >
              {t('mapping:dataTypes.show.edit')}
            </Button>
            <ArchiveBtn type="button">
              {t('mapping:dataTypes.show.archive')}
            </ArchiveBtn>
          </>
        }
      />

      {isLoading && <PageLoaderShow />}
      {error && <div style={{ fontFamily: 'Lato', fontSize: 14, color: MUTED }}>{t('mapping:dataTypes.show.error')}</div>}

      {!isLoading && !error && data && (
        <Grid>
          <InfoCard>
            <InfoCardTitle>{t('mapping:dataTypes.show.infoSectionTitle')}</InfoCardTitle>
            <FieldsWrap>
              <FieldRow>
                <FieldLabel>{t('mapping:dataTypes.show.tags')}</FieldLabel>
                {data.tags?.length > 0
                  ? <TagsWrap>{data.tags.map(tag => <TagChip key={tag}>{tag}</TagChip>)}</TagsWrap>
                  : <FieldValue>—</FieldValue>
                }
              </FieldRow>
              <FieldRow>
                <FieldLabel>{t('mapping:dataTypes.show.description')}</FieldLabel>
                <FieldValue>{data.description || '—'}</FieldValue>
              </FieldRow>
            </FieldsWrap>
          </InfoCard>

          <HistCard>
            <HistTitle>{t('mapping:dataTypes.show.historySectionTitle')}</HistTitle>
            <HistBody>
              <HistItem>
                <FieldLabel>{t('mapping:dataTypes.show.createdAt')}</FieldLabel>
                <HistValue>{formatDate(data.createdAt)}</HistValue>
              </HistItem>
              <HistItem>
                <FieldLabel>{t('mapping:dataTypes.show.updatedAt')}</FieldLabel>
                <HistValue>{formatDate(data.updatedAt)}</HistValue>
              </HistItem>
              <HistItem>
                <FieldLabel>{t('mapping:dataTypes.show.processCount')}</FieldLabel>
                <CountValue>{data.processCount ?? '—'}</CountValue>
              </HistItem>
            </HistBody>
          </HistCard>
        </Grid>
      )}
    </Container>
  );
};
